import { CheckCircleIcon } from "@chakra-ui/icons";
import { Box, Flex, Image, Text } from "@chakra-ui/react";
import { useEthers } from "@usedapp/core";
import axios from "axios";
import BigNumber from "bignumber.js";
import { format } from "date-fns";
import { keys } from "lodash-es";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NftResource } from "../../../components/NftResource";
import { colors } from "../../../core/constants/colors";
import { I18N_NAMESPACE } from "../../../core/constants/i18n";
import { useNft } from "../../../core/hooks/useNft";
import { IMoralisNft } from "../../../core/models/nft";
import { ISale } from "../../../core/models/order";
import { getExplorerTxUrl } from "../../../core/utils/getExplorerUrl";
import { uriToHttps } from "../../../core/utils/ipfs";
import { formatNumber } from "../../../core/utils/number";
import { nftService } from "../../../services/nft.service";
import { useAppSelector } from "../../../store/hook";
import { CompletedOrderDetailModal } from "./CompletedOrderDetailModal";

interface Props {
  order: ISale;
}

export const CompletedOrder = React.memo((props: Props) => {
  const { chainId } = useEthers();
  const { t } = useTranslation([I18N_NAMESPACE.PROFILE]);
  const supportedTokens = useAppSelector((state) => state.token.supportedTokens);
  const token = useMemo(
    () => supportedTokens.byId[props.order.b_token_address],
    [props.order.b_token_address, supportedTokens]
  );

  const [nft, setNft] = useState<IMoralisNft>();
  const isPayInFull = useMemo(() => keys(props.order.payments).length === 1, [props.order.payments]);

  const [isVisibleDetail, setIsVisibleDetail] = useState(false);

  useEffect(() => {
    async function getDetail() {
      const nftResponse = await nftService.getNftDetail(
        props.order.asset_transfer.token_address,
        props.order.asset_transfer.token_id
      );

      const uri = nftResponse.data.token_uri ? uriToHttps(nftResponse.data.token_uri) : undefined;
      const metadataResponse = uri ? await axios.get(uri) : undefined;

      setNft({
        ...nftResponse.data,
        metadata: metadataResponse?.data,
      });
    }

    getDetail();
  }, []);

  const onCloseDetail = () => {
    setIsVisibleDetail(false);
  };

  return (
    <>
      <Flex
        marginTop={"16px"}
        alignItems={"center"}
        borderWidth={1}
        padding={"12px 20px"}
        borderRadius={"xl"}
        gridGap={"20px"}
        cursor={"pointer"}
        onClick={() => setIsVisibleDetail(true)}
      >
        <Flex position={"relative"} height={"50px"} width={"50px"} alignItems={"center"} justifyContent={"center"}>
          <CheckCircleIcon color={colors.success} height="30px" width="30px" />
        </Flex>

        <Box width={"60px"} height={"60px"}>
          <NftResource image={nft?.metadata?.image} animationUrl={nft?.metadata?.animation_url} muted />
        </Box>

        <Box flex={1}>
          <Text fontWeight={"700"}>{nft?.metadata?.name}</Text>
          <Text fontSize={"small"}>
            {props.order.created_time && format(new Date(props.order.created_time), "dd/MM/yyyy")}
          </Text>
        </Box>

        <Box>
          <Flex alignItems="center" justifyContent="flex-end">
            <Image src={token?.logoUrl} height="18px" width="18px" marginRight="4px" />
            <Text fontSize="lg" color={colors.primary1} fontWeight="700">
              {/* {utils.formatUnits(BigInt(props.order.price), token?.decimals)} */}
              {!!token && formatNumber(new BigNumber(props.order.price).div(Math.pow(10, token.decimals)).toNumber())}
            </Text>
          </Flex>
          {!!chainId && isPayInFull && (
            <a href={getExplorerTxUrl(props.order.transaction_hash, chainId)} target={"_blank"}>
              <Text fontSize={"small"} fontWeight={500} cursor={"pointer"} color={colors.primary}>
                {t("View on Explorer")}
              </Text>
            </a>
          )}
        </Box>
      </Flex>
      {!isPayInFull && (
        <CompletedOrderDetailModal
          order={props.order}
          isVisible={isVisibleDetail}
          onClose={onCloseDetail}
          nft={nft}
          token={token}
        />
      )}
    </>
  );
});
